// src/components/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(null);
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [bioOk, setBioOk] = useState(() => localStorage.getItem('auth.bioOk') === 'true');

  const SESSION_LIMIT_S = 5 * 60; // 5 minutos

  // Helpers
  const readDeadline = () => {
    const ts = Number(localStorage.getItem('auth.sessionDeadline') || 0);
    return Number.isFinite(ts) ? ts : 0;
  };
  const writeDeadline = (deadlineMs) => {
    if (deadlineMs) localStorage.setItem('auth.sessionDeadline', String(deadlineMs));
    else localStorage.removeItem('auth.sessionDeadline');
  };
  const computeRemaining = () => {
    const deadline = readDeadline();
    if (!deadline) return null; // sin deadline
    const diffMs = deadline - Date.now();
    return Math.max(0, Math.ceil(diffMs / 1000)); // segundos >= 0
  };

  const logout = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('user');

    localStorage.removeItem('auth.idSesion');
    localStorage.removeItem('auth.hashId');
    localStorage.removeItem('auth.cedula');
    localStorage.removeItem('auth.bioOk');
    writeDeadline(0); // limpiar

    setBioOk(false);
    setTimeLeft(null);
    // Marca opcional para que HeaderPortal muestre modal una sola vez
    localStorage.setItem('auth.autoLoggedOut', 'true');
    console.log('[AUTH DEBUG] Sesión cerrada. Limpieza de almacenamiento completada.');
  }, []);

  const login = (newAccessToken, newUser) => {
    setAccessToken(newAccessToken);
    setUser(newUser);
    sessionStorage.setItem('accessToken', newAccessToken);
    sessionStorage.setItem('user', JSON.stringify(newUser));
  };

  // Llamar desde Login cuando BIOMETRÍA OK
  const markBioOkTrue = () => {
    localStorage.setItem('auth.bioOk', 'true');
    setBioOk(true);
    // Establecer un deadline absoluto para el contador robusto
    const deadline = Date.now() + SESSION_LIMIT_S * 1000;
    writeDeadline(deadline);
    // Refrescar el timeLeft inicial inmediatamente
    const remaining = computeRemaining();
    setTimeLeft(remaining);
    console.log('[AUTH DEBUG] bioOk=true — deadline establecido en', new Date(deadline).toISOString());
  };

  // Init
  useEffect(() => {
    const storedToken = sessionStorage.getItem('accessToken');
    const storedUser = sessionStorage.getItem('user');
    if (storedToken) setAccessToken(storedToken);
    if (storedUser) setUser(JSON.parse(storedUser));
    // Si ya había bioOk y deadline guardado (recarga/refresh)
    if (localStorage.getItem('auth.bioOk') === 'true') {
      const remaining = computeRemaining();
      setTimeLeft(remaining);
      setBioOk(true);
    }
    setAuthReady(true);
  }, []);

  // Escucha cambios de localStorage (otras pestañas o logout)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'auth.bioOk') {
        const newVal = e.newValue === 'true';
        setBioOk(newVal);
        console.log('[AUTH DEBUG] storage event → bioOk =', newVal);
      }
      if (e.key === 'auth.sessionDeadline') {
        // resync immediate
        const remaining = computeRemaining();
        setTimeLeft(remaining);
        console.log('[AUTH DEBUG] storage event → sessionDeadline; remaining =', remaining);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Temporizador robusto basado en deadline absoluto
  useEffect(() => {
    if (!bioOk) {
      setTimeLeft(null);
      writeDeadline(0);
      console.log('[AUTH DEBUG] No hay bioOk; temporizador detenido.');
      return;
    }

    // Tick cada 1s (puede ser menos frecuente por throttling, pero es robusto igual)
    const interval = setInterval(() => {
      const remaining = computeRemaining();
      if (remaining === null) {
        // No hay deadline; detener
        setTimeLeft(null);
        return;
      }
      setTimeLeft(remaining);
      // Logs cada 10s
      if (remaining % 10 === 0) {
        console.log(`[AUTH DEBUG]  Restante: ${remaining}s`);
      }
      if (remaining <= 0) {
        console.log('[AUTH DEBUG]  Sesión expirada (deadline alcanzado). Ejecutando logout.');
        clearInterval(interval);
        logout();
      }
    }, 1000);

    // Recalcular en cambios de visibilidad (al volver del background/sleep)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        const remaining = computeRemaining();
        setTimeLeft(remaining);
        if (remaining !== null) {
          console.log('[AUTH DEBUG] cambio de focus → restante: =', remaining);
        }
        if (remaining !== null && remaining <= 0) {
          logout();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [bioOk, logout]);

  // Debug
  useEffect(() => {
    console.log('[AUTH DEBUG] accessToken:', accessToken || '(null)');
    console.log('[AUTH DEBUG] bioOk:', bioOk);
  }, [accessToken, bioOk]);

  return (
    <AuthContext.Provider
      value={{ accessToken, user, login, logout, authReady, timeLeft, bioOk, markBioOkTrue }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
